import {
  CancelReasons,
  CustomFields,
  Entity,
  Location,
  LocationMapper,
  Report,
  ReportImageProps,
} from '@nx-smartmonkey/shared/domain';
import { removeEmpty } from '@nx-smartmonkey/shared/helpers';
import { CustomersSurvey } from '../customersSurvey/CustomersSurvey';
import { Event } from '../event/Event';
import { Task } from '../task/Task';
import { StopStatus } from './StopStatus';
import { StopTypes } from './StopTypes';

export interface StopUpdateProps {
  client_id?: string;
  client_external_id?: string;
  external_id?: string;
  location?: Location | null;
  location_details?: string;
  label?: string;
  status?: StopStatus;
  comments?: string;
  phone?: string;
  email?: string;
  url?: string;
  reference_person?: string;

  // Constraints
  duration?: number;
  requires?: string[];
  time_windows?: Array<[number, number]>;
  volume?: number;
  weight?: number;
  max_delivery_time?: number;
  cluster?: string;
  reward?: number;

  driver_id?: string;
  custom_fields?: CustomFields;
  supervisors?: string[];
}

export interface StopProps {
  project_id: string;
  plan_id?: string;
  client_id?: string;
  client_external_id?: string;
  label?: string;
  external_id?: string;
  type: StopTypes;
  status: StopStatus;
  location?: Location | null;
  location_details?: string;
  comments?: string;
  reference_person?: string;
  phone?: string;
  email?: string;
  url?: string;
  custom_fields?: CustomFields;
  weight?: number;
  volume?: number;
  time_windows?: Array<[number, number]>;
  requires?: string[];
  duration?: number;
  max_delivery_time?: number;
  cluster?: string;
  reward?: number;
  driver_id?: string;
  vehicle_id?: string;
  order?: number;
  planned_arrival_time?: number;
  planned_departure_time?: number;
  distance_to_previous_stop?: number;
  distance_to_next_stop?: number;
  estimated_arrival_time?: Date;
  estimated_departure_time?: Date;
  pickup_id?: string;
  pickup?: Stop;
  tasks?: Task[];
  events?: Event[];
  reports?: Report[];
  survey?: CustomersSurvey;
  pin?: string;
  customer_text_field?: string;
  supervisors: string[];
  deleted?: boolean;
  created_by?: string;
  created_at?: Date;
  updated_at?: Date;
  deleted_at?: Date;
}

export class Stop extends Entity<StopProps> {
  get id(): string {
    return this._id!;
  }
  get project_id(): string {
    return this.props.project_id;
  }
  set project_id(projectId: string) {
    this.props.project_id = projectId;
  }
  get plan_id(): string | undefined {
    return this.props.plan_id;
  }
  set plan_id(projectId: string | undefined) {
    this.props.plan_id = projectId;
  }
  get client_id(): string | undefined {
    return this.props.client_id;
  }
  set client_id(client_id: string | undefined) {
    this.props.client_id = client_id;
  }
  get client_external_id(): string | undefined {
    return this.props.client_external_id;
  }
  set client_external_id(client_external_id: string | undefined) {
    this.props.client_external_id = client_external_id;
  }
  get label(): string | undefined {
    return this.props.label;
  }
  set label(label: string | undefined) {
    this.props.label = label;
  }
  get external_id(): string | undefined {
    return this.props.external_id;
  }
  set external_id(external_id: string | undefined) {
    this.props.external_id = external_id;
  }
  get type(): StopTypes {
    return this.props.type;
  }
  set type(type: StopTypes) {
    this.props.type = type;
  }
  get status(): StopStatus {
    return this.props.status;
  }
  set status(status: StopStatus) {
    this.props.status = status;
  }
  get location(): Location | undefined | null {
    return this.props.location;
  }
  set location(location: Location | undefined | null) {
    this.props.location = location;
  }
  get location_details(): string | undefined {
    return this.props.location_details;
  }
  set location_details(location_details: string | undefined) {
    this.props.location_details = location_details;
  }
  get comments(): string | undefined {
    return this.props.comments;
  }
  set comments(comments: string | undefined) {
    this.props.comments = comments;
  }
  get reference_person(): string | undefined {
    return this.props.reference_person;
  }
  set reference_person(reference_person: string | undefined) {
    this.props.reference_person = reference_person;
  }
  get phone(): string | undefined {
    return this.props.phone;
  }
  set phone(phone: string | undefined) {
    this.props.phone = phone;
  }
  get email(): string | undefined {
    return this.props.email;
  }
  set email(email: string | undefined) {
    this.props.email = email;
  }
  get url(): string | undefined {
    return this.props.url;
  }
  set url(url: string | undefined) {
    this.props.url = url;
  }
  get custom_fields(): CustomFields | undefined {
    return this.props.custom_fields;
  }
  set custom_fields(custom_fields: CustomFields | undefined) {
    this.props.custom_fields = custom_fields;
  }
  get weight(): number | undefined {
    return this.props.weight;
  }
  set weight(weight: number | undefined) {
    this.props.weight = weight;
  }
  get volume(): number | undefined {
    return this.props.volume;
  }
  set volume(volume: number | undefined) {
    this.props.volume = volume;
  }
  get time_windows(): Array<[number, number]> | undefined {
    return this.props.time_windows;
  }
  set time_windows(time_windows: Array<[number, number]> | undefined) {
    this.props.time_windows = time_windows;
  }
  get requires(): string[] | undefined {
    return this.props.requires;
  }
  set requires(requires: string[] | undefined) {
    this.props.requires = requires;
  }
  get duration(): number | undefined {
    return this.props.duration;
  }
  set duration(duration: number | undefined) {
    this.props.duration = duration;
  }
  get max_delivery_time(): number | undefined {
    return this.props.max_delivery_time;
  }
  set max_delivery_time(max_delivery_time: number | undefined) {
    this.props.max_delivery_time = max_delivery_time;
  }
  get cluster(): string | undefined {
    return this.props.cluster;
  }
  set cluster(cluster: string | undefined) {
    this.props.cluster = cluster;
  }
  get reward(): number | undefined {
    return this.props.reward;
  }
  set reward(reward: number | undefined) {
    this.props.reward = reward;
  }
  get driver_id(): string | undefined {
    return this.props.driver_id;
  }
  set driver_id(driver_id: string | undefined) {
    this.props.driver_id = driver_id;
  }
  get vehicle_id(): string | undefined {
    return this.props.vehicle_id;
  }
  get order(): number | undefined {
    return this.props.order;
  }
  set order(order: number | undefined) {
    this.props.order = order;
  }
  get planned_arrival_time(): number | undefined {
    return this.props.planned_arrival_time;
  }
  set planned_arrival_time(planned_arrival_time: number | undefined) {
    this.props.planned_arrival_time = planned_arrival_time;
  }
  get planned_departure_time(): number | undefined {
    return this.props.planned_departure_time;
  }
  set planned_departure_time(planned_departure_time: number | undefined) {
    this.props.planned_departure_time = planned_departure_time;
  }
  get distance_to_previous_stop(): number | undefined {
    return this.props.distance_to_previous_stop;
  }
  set distance_to_previous_stop(distance_to_previous_stop: number | undefined) {
    this.props.distance_to_previous_stop = distance_to_previous_stop;
  }
  get distance_to_next_stop(): number | undefined {
    return this.props.distance_to_next_stop;
  }
  set distance_to_next_stop(distance_to_next_stop: number | undefined) {
    this.props.distance_to_next_stop = distance_to_next_stop;
  }
  get estimated_arrival_time(): Date | undefined {
    return this.props.estimated_arrival_time;
  }
  set estimated_arrival_time(estimated_arrival_time: Date | undefined) {
    this.props.estimated_arrival_time = estimated_arrival_time;
  }
  get estimated_departure_time(): Date | undefined {
    return this.props.estimated_departure_time;
  }
  set estimated_departure_time(estimated_departure_time: Date | undefined) {
    this.props.estimated_departure_time = estimated_departure_time;
  }
  get pickup_id(): string | undefined {
    return this.props.pickup_id;
  }
  set pickup_id(pickup_id: string | undefined) {
    this.props.pickup_id = pickup_id;
  }
  get pickup(): Stop | undefined {
    return this.props.pickup;
  }
  set pickup(pickup: Stop | undefined) {
    this.props.pickup = pickup;
  }
  get tasks(): Task[] | undefined {
    return this.props.tasks;
  }
  set tasks(tasks: Task[] | undefined) {
    this.props.tasks = tasks;
  }
  get events(): Event[] | undefined {
    return this.props.events;
  }
  set events(events: Event[] | undefined) {
    this.props.events = events;
  }
  get reports(): Report[] | undefined {
    return this.props.reports;
  }
  set reports(reports: Report[] | undefined) {
    this.props.reports = reports;
  }
  get survey(): CustomersSurvey | undefined {
    return this.props.survey;
  }
  set survey(survey: CustomersSurvey | undefined) {
    this.props.survey = survey;
  }
  get pin(): string | undefined {
    return this.props.pin;
  }
  get customer_text_field(): string | undefined {
    return this.props.customer_text_field;
  }
  get supervisors(): string[] {
    return this.props.supervisors;
  }
  get deleted(): boolean | undefined {
    return this.props.deleted;
  }
  get created_by(): string | undefined {
    return this.props.created_by;
  }
  set created_by(created_by: string | undefined) {
    this.props.created_by = created_by;
  }
  get created_at(): Date | undefined {
    return this.props.created_at;
  }
  set created_at(created_at: Date | undefined) {
    this.props.created_at = created_at;
  }
  get updated_at(): Date | undefined {
    return this.props.updated_at;
  }
  set updated_at(updated_at: Date | undefined) {
    this.props.updated_at = updated_at;
  }
  get deleted_at(): Date | undefined {
    return this.props.deleted_at;
  }
  set deleted_at(deleted_at: Date | undefined) {
    this.props.deleted_at = deleted_at;
  }
  update(props: StopUpdateProps) {
    removeEmpty(props);

    if (props.client_id !== undefined) this.props.client_id = props.client_id;
    if (props.client_external_id !== undefined) this.props.client_external_id = props.client_external_id;
    if (props.external_id !== undefined) this.props.external_id = props.external_id;
    if (props.location !== undefined) this.props.location = props.location;
    if (props.location_details !== undefined) this.props.location_details = props.location_details;
    if (props.label !== undefined) this.props.label = props.label;
    if (props.status !== undefined) this.props.status = props.status!;
    if (props.comments !== undefined) this.props.comments = props.comments;
    if (props.phone !== undefined) this.props.phone = props.phone;
    if (props.email !== undefined) this.props.email = props.email;
    if (props.url !== undefined) this.props.url = props.url;
    if (props.reference_person !== undefined) this.props.reference_person = props.reference_person;

    // Constraints
    if (props.duration !== undefined) this.props.duration = props.duration;
    if (props.requires !== undefined) this.props.requires = props.requires;
    if (props.time_windows !== undefined) this.props.time_windows = props.time_windows;
    if (props.volume !== undefined) this.props.volume = props.volume;
    if (props.weight !== undefined) this.props.weight = props.weight;
    if (props.max_delivery_time !== undefined) this.props.max_delivery_time = props.max_delivery_time;
    if (props.reward !== undefined) this.props.reward = props.reward;
    if (props.cluster !== undefined) this.props.cluster = props.cluster;

    if (props.driver_id !== undefined) this.props.driver_id = props.driver_id;
    if (props.custom_fields !== undefined) this.props.custom_fields = props.custom_fields;
    if (props.supervisors !== undefined) this.props.supervisors = props.supervisors;

    this.props.updated_at = new Date();
  }
  private constructor(props: StopProps, id?: string) {
    super(props, id);
  }
  static create(props: StopProps, id?: string): Stop {
    return new Stop(props, id);
  }

  deletePickup() {
    this.pickup = undefined;
    this.pickup_id = undefined;
  }

  addPickup(pickup: Stop) {
    this.pickup = pickup;
    this.pickup_id = pickup.id;
  }

  unassign() {
    this.props.driver_id = undefined;
    this.props.order = undefined;
    this.props.distance_to_next_stop = undefined;
    this.props.distance_to_previous_stop = undefined;
    this.props.planned_arrival_time = undefined;
    this.props.planned_departure_time = undefined;
    this.props.estimated_arrival_time = undefined;
    this.props.estimated_departure_time = undefined;
  }

  getStopReport(planId?: string): Report | undefined {
    if (!planId) {
      return undefined;
    }
    const stopReport = this.reports?.find((report) => report.plan_id === planId);
    return stopReport ?? undefined;
  }

  getStopDoneAt(planId?: string): Date | undefined {
    const planReport = this.getStopReport(planId);
    return planReport?.created_at;
  }

  getStopDoneLocation(planId?: string): Location | undefined {
    const planReport = this.getStopReport(planId);
    return planReport?.location ? LocationMapper.toDomain(planReport.location) : undefined;
  }

  getStopReportComments(planId?: string): string | undefined {
    const planReport = this.getStopReport(planId);
    return planReport?.comments;
  }

  getStopReportTimeInStop(planId?: string): number | undefined {
    const planReport = this.getStopReport(planId);
    return planReport?.time_in_stop;
  }

  getStopReportImages(planId?: string): ReportImageProps[] | undefined {
    const planReport = this.getStopReport(planId);
    return planReport?.images;
  }

  getStopReportSignature(planId?: string): ReportImageProps | undefined {
    const planReport = this.getStopReport(planId);
    return planReport?.signature;
  }

  getStopReportCancelReason(planId?: string): CancelReasons | undefined {
    const planReport = this.getStopReport(planId);
    return planReport?.cancel_reason;
  }

  getStopReportCustomFields(planId?: string): CustomFields | undefined {
    const planReport = this.getStopReport(planId);
    return planReport?.custom_fields;
  }

  getCurrentReport(): Report | undefined {
    if (this.reports && this.reports.length > 0) {
      const currentReport = this.reports.find((report) => report.plan_id === this.plan_id);
      return currentReport;
    }
    return undefined;
  }
}
